import { useEffect, useMemo, useState } from 'react'
import type { PortfolioSnapshot, Position, RegimeState, StrategyConfig } from '../../lib/supabase'
import { GatesStrip } from './GatesStrip'
import { ResearchOutcomes } from './ResearchOutcomes'
import { SleeveLadder } from './SleeveLadder'
import { buildSleeves, type SleeveKey } from './strategyContent'
import { useStrategyLive } from './useStrategyLive'
import './strategy.css'

interface Props {
  snapshot: PortfolioSnapshot | null
  positions: Position[]
  regimeState: RegimeState | null
  strategyConfig: StrategyConfig | null
  isDemoMode: boolean
}

const NARROW_QUERY = '(max-width: 767px)'

function useNarrow(): boolean {
  const get = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(NARROW_QUERY).matches : false)
  const [narrow, setNarrow] = useState(get)
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mq = window.matchMedia(NARROW_QUERY)
    const on = () => setNarrow(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return narrow
}

// EST label: the dashboard states times in EST year-round.
function estStamp(d: Date): string {
  const t = new Date(d.getTime() - 5 * 3600 * 1000)
  return `${t.toISOString().slice(0, 16).replace('T', ' ')} EST`
}

export function StrategyView({ snapshot, positions, regimeState, strategyConfig, isDemoMode }: Props) {
  const narrow = useNarrow()
  const sleeves = useMemo(() => buildSleeves(strategyConfig), [strategyConfig])
  const fallbackTargets = useMemo(
    () => Object.fromEntries(sleeves.map(s => [s.key, s.fallbackTarget])) as Record<SleeveKey, number>, [sleeves])
  const today = new Date().toLocaleDateString('en-CA')
  const live = useStrategyLive(positions, snapshot, regimeState, strategyConfig, fallbackTargets, isDemoMode, today)
  const [open, setOpen] = useState<Record<string, boolean>>({ etf: true })
  const totalTarget = sleeves.reduce((a, s) => a + live.sleeves[s.key].targetPct!, 0)

  return (
    <div className="strat">
      <header className="strat-header">
        <p className="strat-eyebrow">JGL Capital, live entry rules as coded</p>
        <h1 className="strat-h1">How a name gets into each sleeve</h1>
        <p className="strat-lede">
          Five sleeves, stacked by target weight ({totalTarget.toFixed(1)}% of NAV in total). Read each ladder left to
          right: the broadest filter first, the most specific last, and the GO card is the moment it buys. A name has to
          clear every active rung. Rungs marked removed no longer gate entries.
        </p>
        <ul className="strat-legend" aria-label="Legend">
          <li><span className="strat-chip">static</span> fixed number</li>
          <li><span className="strat-chip strat-chip--dynamic">dynamic</span> moves with the market</li>
          <li><span className="strat-chip strat-chip--review">under review</span> live, not as intended</li>
          <li><span className="strat-chip strat-chip--removed">removed, D079</span> no longer gates entries</li>
        </ul>
        {live.reason ? (
          <p className="strat-status strat-status--warn" role="status">{live.reason}</p>
        ) : (
          <p className={`strat-status${live.stale ? ' strat-status--warn' : ''}`} role="status">
            {live.stale ? 'Stale: ' : ''}Live numbers from the portfolio snapshot of {live.asOf}
            {live.regimeDate ? ` and the regime row of ${live.regimeDate}` : ' (no regime row: scalars unavailable)'}.
            Page rendered {estStamp(new Date())}.
          </p>
        )}
      </header>

      <GatesStrip />

      <section className="strat-section" aria-labelledby="strat-sleeves-h">
        <h2 id="strat-sleeves-h" className="strat-h2">The five sleeves</h2>
        {sleeves.map(s => (
          <SleeveLadder
            key={s.key}
            sleeve={s}
            live={live.sleeves[s.key]}
            liveAvailable={live.available}
            narrow={narrow}
            open={!!open[s.key]}
            onToggle={() => setOpen(o => ({ ...o, [s.key]: !o[s.key] }))}
          />
        ))}
      </section>

      <section className="strat-section" aria-label="Research and caveats">
        <ResearchOutcomes narrow={narrow} />
      </section>
    </div>
  )
}
